/**
 * Phase 5 §7 — SCHADS engine wrapper.
 *
 * **Refactor strategy: thin wrapper, not relocation.** The Phase 1
 * `calculateShiftPay` and `calculateShiftPayWithOvertime` bodies live
 * in `../schads.ts` and are NOT moved. This wrapper exposes them via
 * the `AwardEngine` interface. The 49 dual-run regression fixtures
 * (`packages/utils/src/__tests__/dual-run.test.ts`) prove byte-equality
 * of the SCHADS path; wrapping rather than moving keeps that proof
 * intact.
 *
 * The wrapper accepts the canonical Phase 1 `RateContext`. When ctx
 * is provided, it is converted to a `SchadsRatesOverride` via the
 * existing `buildOverrideFromContext` helper (the dual-run test
 * verifies this conversion is byte-equal to the constants path). When
 * ctx is undefined, the legacy function selects the dated national-catalogue fallback
 * for the shift start in its tenant timezone.
 */

import {
  buildOverrideFromContext,
  collectRequiredAllowanceCoverageGaps,
  collectRequiredPenaltyCoverageGaps,
  coverageGapsField,
  type RateContext,
} from '../award-rate-lookup';
import { roundMoney, sumMoney } from '../money-rounding';
import {
  schadsShiftLoadingCharacters,
  calculateShiftPay as legacyCalculateShiftPay,
  calculateShiftPayWithOvertime as legacyCalculateShiftPayWithOvertime,
  type ShiftPayBreakdown,
  type ShiftPayInput,
  type ShiftPayWithOvertimeInput,
} from '../schads';
import { calculateRecallPay, type RecallPayBreakdown, type RecallPayInput } from './recall';
import type { AwardEngine, AwardCode } from './types';
import {
  SCHADS_RECALL_RULES,
  SCHADS_CASUAL_PENALTY_RATES,
  SCHADS_PERMANENT_PENALTY_RATES,
} from '../award-data/schads';
import { classifyShift } from './shift-classifier';
import { applyDstStartOrdinaryTopUp } from '../payroll/multi-zone-pay-clock';
import {
  buildNativeEvaluationTrace,
  unavailableEvaluationTrace,
  type EvaluationTrace,
  type TracePrice,
} from './evaluation-trace';

const round2 = (n: number): number => roundMoney(n);

const SCHADS_ALLOWANCE_EMISSION_CODES_BY_RATE_CODE: Record<string, string[]> = {
  SLEEPOVER: ['SLEEPOVER_ALLOWANCE'],
  BROKEN_SHIFT_1: ['BROKEN_SHIFT_SINGLE_ALLOWANCE'],
  BROKEN_SHIFT_2_PLUS: ['BROKEN_SHIFT_MULTIPLE_ALLOWANCE'],
  KM: ['KM_ALLOWANCE', 'KM_ALLOWANCE_EXEMPT', 'KM_ALLOWANCE_TAXABLE'],
  ON_CALL_WEEKDAY: ['ON_CALL_WEEKDAY_ALLOWANCE'],
  ON_CALL_WEEKEND: ['ON_CALL_WEEKEND_ALLOWANCE'],
  MEAL: ['MEAL_ALLOWANCE'],
  FIRST_AID_FT_WEEKLY: ['FIRST_AID_ALLOWANCE'],
  FIRST_AID_PT_HOURLY: ['FIRST_AID_ALLOWANCE'],
  UNIFORM_PER_SHIFT: ['UNIFORM_ALLOWANCE'],
  LAUNDRY_PER_SHIFT: ['LAUNDRY_ALLOWANCE'],
};

function requiredSchadsAllowanceCodesForInput(
  input: ShiftPayInput | ShiftPayWithOvertimeInput,
): string[] {
  const codes: string[] = [];
  if (input.hasSleepover) codes.push('SLEEPOVER');
  if (input.hasBrokenShift === 1) codes.push('BROKEN_SHIFT_1');
  if (input.hasBrokenShift === 2) codes.push('BROKEN_SHIFT_2_PLUS');
  if ((input.travelKm ?? 0) > 0) codes.push('KM');
  if ((input.onCallWeekdayDays ?? 0) > 0) codes.push('ON_CALL_WEEKDAY');
  if ((input.onCallWeekendDays ?? 0) > 0) codes.push('ON_CALL_WEEKEND');
  if (input.hasDelayedMealAllowance) codes.push('MEAL');
  if (input.hasFirstAidResponsibility) {
    codes.push(input.isFullTime === true ? 'FIRST_AID_FT_WEEKLY' : 'FIRST_AID_PT_HOURLY');
  }
  if (input.hasUniformAllowance) codes.push('UNIFORM_PER_SHIFT');
  if (input.hasLaundryAllowance) codes.push('LAUNDRY_PER_SHIFT');
  return codes;
}

function collectSchadsEbaCoverageGaps(
  ctx: RateContext,
  input: ShiftPayInput | ShiftPayWithOvertimeInput,
  employment: 'PERMANENT' | 'CASUAL',
) {
  return [
    ...collectRequiredPenaltyCoverageGaps(
      ctx,
      ['SATURDAY', 'SUNDAY', 'PUBLIC_HOLIDAY', 'WEEKDAY_EVENING', 'WEEKDAY_NIGHT'],
      employment,
    ),
    ...collectRequiredAllowanceCoverageGaps(
      ctx,
      requiredSchadsAllowanceCodesForInput(input),
      employment,
    ),
  ];
}

function removeMissingRequiredAllowanceFallbackLines(
  breakdown: ShiftPayBreakdown,
  coverageGaps: ReturnType<typeof collectSchadsEbaCoverageGaps>,
): ShiftPayBreakdown {
  const missingEmissionCodes = new Set(
    coverageGaps.flatMap((gap) =>
      gap.allowanceCode
        ? (SCHADS_ALLOWANCE_EMISSION_CODES_BY_RATE_CODE[gap.allowanceCode] ?? [])
        : [],
    ),
  );
  if (missingEmissionCodes.size === 0) return breakdown;

  const allowanceLines = breakdown.allowanceLines.filter(
    (line) => !missingEmissionCodes.has(line.code),
  );
  if (allowanceLines.length === breakdown.allowanceLines.length) return breakdown;

  // Warn with the codes ACTUALLY removed from THIS breakdown, not the whole
  // configured emission set for the gapped rate code. Otherwise adding emission
  // codes to a rate code's list (e.g. the km split's EXEMPT/TAXABLE alongside
  // KM_ALLOWANCE) would change this warning's text for unrelated single-line
  // callers with the flag OFF — breaking flag-off byte-identity.
  const removedCodes = Array.from(
    new Set(
      breakdown.allowanceLines
        .filter((line) => missingEmissionCodes.has(line.code))
        .map((line) => line.code),
    ),
  ).sort();

  const allowancesPay = round2(allowanceLines.reduce((sum, line) => sum + line.amount, 0));
  const grossTotal = Number.isFinite(breakdown.grossTotal)
    ? sumMoney(
        breakdown.basePay,
        breakdown.penaltyPay,
        breakdown.shiftLoadingPay,
        breakdown.overtimePay,
        allowancesPay,
      )
    : breakdown.grossTotal;

  return {
    ...breakdown,
    allowancesPay,
    allowanceLines,
    grossTotal,
    warnings: [
      ...breakdown.warnings,
      `Required EBA allowance coverage gap removed legacy fallback allowance line(s): ${removedCodes.join(
        ', ',
      )}.`,
    ],
  };
}

/**
 * Semver of the SCHADS Award (MA000100) engine. Bumped manually on every
 * math-affecting change to this wrapper OR to the legacy
 * `packages/utils/src/schads.ts` body it delegates to. Persisted to
 * `pay_run_items.snapshot_engine_version` via `finalise_pay_run_snapshot`,
 * so each row is byte-reproducible against the engine code that produced it.
 *
 * Enforced by `.husky/pre-commit` + CI assertion: any modification to this
 * file OR to `packages/utils/src/schads.ts` (other than the version line
 * itself) requires this string to change, unless the commit message contains
 * `[engine-no-math-change]`.
 */
// Phase W4.0.5 (2026-05-17) — bumped 5.5.7 → 5.5.8 to reflect the
// ShiftPayBreakdown contract evolution: new optional `leadingHandAllPurposeLift`
// evidence field added to the shared `ShiftPayBreakdown` interface in
// `../schads.ts`. SCHADS engine math UNCHANGED — only the type contract
// expanded.
//
// Phase 1N (2026-05-17) — bumped 5.5.8 → 5.6.0 (semver MINOR). Output contract
// change per `project_allowance_code_naming_taxonomy.md`. SCHADS legacy body
// (`../schads.ts:buildAllowanceLines`) emits canonical _ALLOWANCE-suffixed
// codes (SLEEPOVER → SLEEPOVER_ALLOWANCE; BROKEN_SHIFT splits into
// BROKEN_SHIFT_SINGLE_ALLOWANCE per cl 20.12(a) and BROKEN_SHIFT_MULTIPLE_ALLOWANCE
// per cl 20.12(b); TRAVEL_KM → KM_ALLOWANCE; DELAYED_MEAL → MEAL_ALLOWANCE;
// ON_CALL_WEEKDAY/WEEKEND → _ALLOWANCE; FIRST_AID/UNIFORM/LAUNDRY → _ALLOWANCE).
// SCHADS math UNCHANGED.
//
// Phase 3 PR #3 sub-phase 3G (2026-05-18) — bumped 5.6.0 → 5.6.1 (semver PATCH).
// New LEAVE_LOADING_ALLOWANCE emission per cl 31.4(a) gated on
// segmentKind === 'paid_annual_leave' AND non-casual employmentType. Flat
// 17.5% × ordinary leave pay; cl 31.4(b) higher-of is applied by the
// pay-run leave-snapshot layer.
// All pre-3G callers default segmentKind='work' → byte-equal for work
// segments. T2 sweep + Path-A pins cover the new emission path.
//
// Phase Codex Pass A PR #B (2026-05-19) — bumped 5.6.1 → 5.7.0 (semver MINOR).
// [engine-math-changed] tag — `calculateRecall` math materially changed
// per FWC primary-source verification. Pre-fix `SchadsEngine.calculateRecall`
// collapsed all paid hours to a single flat 1.5× tier-1 multiplier; post-fix
// applies banded OT (tier-1 first 2h or 3h depending on FT stream, then
// tier-2 for the remainder) plus MULTIPLICATIVE casual loading (1.875× /
// 2.5× / 2.5× / 3.125× per band). `calculate` + `calculateWithOvertime`
// (timesheet path) are UNCHANGED — only recall pay is affected.
// Artifact: docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-recall-banded-ot.md
//
// R6 Golden Bloom (2026-06-01) — bumped 5.7.0 → 5.8.0 (semver MINOR).
// [engine-math-changed] First-aid allowance emission is now gated by the
// worker certification registry when callers supply certGateRegistry +
// activeCertifications. Legacy callers without a registry remain byte-equal.
// Artifact: docs/operations/clause-verifications/2026-06-01-aged-care-legislation-schads-first-aid-cert-gate.md
//
// EA ordinary-hours cycle bridge (2026-06-09) — bumped 5.8.0 → 5.8.1
// (semver PATCH). `calculateWithOvertime` now accepts configured ordinary
// cycle caps and can disable synthetic weekly accumulation for non-7-day EA
// cycles. Legacy callers without cycle inputs remain byte-equal.
//
// EBA Phase 2 strict-mode (2026-07-03) — bumped 5.8.3 → 5.8.4. The SCHADS
// ctx override bridge honors explicit requiredBands supplied in ctx, but the
// EBA block compiler only wires automated penalty overlays for MA000018/
// MA000034 in Phase 2; MA000100 overlays without a compile profile now block
// structurally. Non-EBA output is byte-equal by the Phase 2 golden.
//
// EBA Phase 3 follow-up (2026-07-04) — bumped 5.8.4 → 5.8.5. Required EBA
// allowance rows now fail loud through coverageGaps when a triggered SCHADS
// allowance lookup would otherwise be satisfied by legacy award constants.
// Non-EBA output is byte-equal.
//
// EBA Phase 3 follow-up (2026-07-04) — bumped 5.8.5 → 5.8.6. Recall and
// sleepover disturbance penalty multipliers now fail loud through coverageGaps
// when a required EBA penalty row is missing.
//
// Concurrent ordinary-hours windows (2026-07-14) — bumped 5.8.8 → 5.8.9.
// Effective provider-configured cap windows now constrain shared cumulative
// ordinary-hour headroom. Legacy callers without the new input remain byte-equal.
//
// Vehicle km STP tax split (2026-07-20) — bumped 5.8.9 → 5.9.0. When
// `input.vehicleKmTaxSplit` is set, the cl 20.7(a) km allowance emits two
// STP-'CD' lines (PAYGW-exempt + taxable residual) instead of one KM_ALLOWANCE
// line; the 5,000 km/FY exemption cap is applied at the pay-run layer. Callers
// without the flag remain byte-equal (single KM_ALLOWANCE line unchanged).
//
// PR798459 sleepover rest/loadings/OT (2026-08-15) — bumped 5.9.0 → 5.9.1.
// Date-gated from 1 June 2026. Callers without sleepover window / 12h
// agreement / flank flags remain byte-equal.
//
// SCHADS cl 25.10 remote work (2026-08-15) — bumped 5.9.1 → 5.9.2 on the
// programme stack (focused E2 PR is 5.9.1 off main). Callers without
// isRemoteWork remain byte-equal.
//
// Wage-related allowances from percentages (2026-08-15) — bumped 5.9.2 → 5.9.3
// cl 25.10(d)(i)(C) OT ladder uses overtime hours (2026-08-16) — 5.9.3 → 5.9.4.
// on the programme stack (focused E6 PR is 5.9.1 off main). From 1 July 2026
// the constants path computes sleepover / on-call / first-aid / broken-shift
// from cl 15.3 minimum-column $1,283.10. RateContext override still wins.
// MZ-004 D5 (2026-10-05) — 5.9.10 → 5.9.21. Wrapper-only: a permanent work
// segment crossing a DST-start gap gains a separate DST-start ordinary top-up
// line. The legacy schads.ts body is untouched; all else byte-equal. 5.9.21,
// not 5.9.11: two other local worktrees (schads-cl29-3-loading-20261005,
// disturbance-midnight-tz-20261005) already claim 5.9.11.
// Lane A: combine inherited MZ-004 with cl 29.3 whole-work-period loading.
// S0-05 Round 2: both-clock calendar spans feed the shared classifier.
// Half-cent rounding (2026-10-08) — 5.9.22 → 5.9.23. round2 now uses the shared
// exact half-up roundMoney (money-rounding.ts) instead of Math.round on the
// binary float, so an exact half-cent (e.g. $525.335) pays $525.34, not
// $525.33, matching the evaluation trace's BigInt half-up. Output changes by
// +1c only where an amount is an exact half-cent. No clause, rate or multiplier change.
// ENG1 Round 7: shared trace period boundaries and overtime input contract.
export const ENGINE_VERSION = '5.9.25';

export class SchadsEngine implements AwardEngine {
  readonly awardCode: AwardCode = 'MA000100';
  readonly engineVersion: string = ENGINE_VERSION;
  readonly engineRevisionSha: string = process.env.STATURA_ENGINE_REVISION ?? 'dev';
  buildEvaluationTrace(
    input: ShiftPayInput | ShiftPayWithOvertimeInput,
    ctx?: RateContext,
  ): EvaluationTrace {
    if (
      input.hasSleepover ||
      input.shiftLoadingContext?.hasSleepover ||
      input.isRemoteWork ||
      'ordinaryHoursLimitPerShift' in input
    ) {
      return unavailableEvaluationTrace('SCHADS_COMPLEX_INTERVAL_PRICE_NOT_RETAINED');
    }
    const timezone = input.timezone;
    if (!timezone) return unavailableEvaluationTrace('SCHADS_TENANT_TIMEZONE_REQUIRED');
    const breakdown = this.calculate(input, ctx);
    if (breakdown.baseHourlyRate <= 0 || breakdown.overtimeHours > 0) {
      return unavailableEvaluationTrace(
        breakdown.baseHourlyRate <= 0
          ? 'SCHADS_RATE_UNRECOGNISED'
          : 'SCHADS_OVERTIME_INTERVAL_PRICE_NOT_RETAINED',
      );
    }
    const override = ctx ? buildOverrideFromContext(ctx) : undefined;
    const rates =
      input.employmentType === 'casual'
        ? (override?.casualPenaltyRates ?? SCHADS_CASUAL_PENALTY_RATES)
        : (override?.permanentPenaltyRates ?? SCHADS_PERMANENT_PENALTY_RATES);
    const casualMultiplier = input.employmentType === 'casual' ? 1.25 : 1;
    const classified = classifyShift({
      startTime: input.startTime,
      endTime: input.endTime,
      breakMinutes: 0,
      timezone,
      isPublicHoliday: input.isPublicHoliday ?? false,
      publicHolidaySegments: input.publicHolidaySegments,
      shiftCharacterRules: {
        fallbackPerSegmentBands: {
          eveningStartHour: 20,
          nightStartHour: 0,
          nightEndHourExclusive: 6,
        },
      },
      dominantDayRule: 'PER_SEGMENT',
    });
    const loadingCharacter = schadsShiftLoadingCharacters(input).undivided;
    const price = (at: Date): TracePrice => {
      const cell = classified.segments.find(
        (segment) =>
          segment.startTime.getTime() <= at.getTime() && at.getTime() < segment.endTime.getTime(),
      );
      if (!cell) throw new Error('SCHADS_CLASSIFIER_CELL_MISSING');
      let multiplier = casualMultiplier;
      let band = 'WEEKDAY_DAY';
      let component = 'ORDINARY';
      if (cell.dayType === 'saturday') {
        multiplier = rates.saturdayOrdinary;
        band = 'SATURDAY';
      } else if (cell.dayType === 'sunday') {
        multiplier = rates.sundayOrdinary;
        band = 'SUNDAY';
      } else if (cell.dayType === 'publicHoliday') {
        multiplier = rates.publicHoliday;
        band = 'PUBLIC_HOLIDAY';
      } else if (input.employeeCategory === 'shiftworker' && loadingCharacter === 'AFTERNOON') {
        multiplier = rates.afternoonShiftLoading;
        band = 'WEEKDAY_EVENING';
        component = 'ORDINARY_PLUS_SHIFT_LOADING';
      } else if (input.employeeCategory === 'shiftworker' && loadingCharacter === 'NIGHT') {
        multiplier = rates.nightAfter12am;
        band = 'WEEKDAY_NIGHT';
        component = 'ORDINARY_PLUS_SHIFT_LOADING';
      }
      // eslint-disable-next-line statura/no-floating-point-money -- converts the engine-selected decimal rate to integer cents6 for trace evidence.
      const rateCents6 = Math.round(breakdown.baseHourlyRate * multiplier * 100 * 1_000_000);
      // SCHADS rounds basePay (every hour at base × casual multiplier),
      // penaltyPay (weekend/PH premium above it) and shiftLoadingPay separately
      // (Astra RND-02): the trace reconciles on those same groups.
      // eslint-disable-next-line statura/no-floating-point-money -- integer cents6 share of the engine-selected rate.
      const baseCents6 = Math.round(breakdown.baseHourlyRate * casualMultiplier * 100 * 1_000_000);
      const premiumGroup =
        component === 'ORDINARY_PLUS_SHIFT_LOADING' ? 'SHIFT_LOADING' : 'PENALTY';
      return {
        band,
        component,
        multiplierBasisPoints: Math.round(multiplier * 10_000),
        rateCents6,
        components:
          rateCents6 === baseCents6
            ? [{ group: 'BASE', rateCents6 }]
            : [
                { group: 'BASE', rateCents6: baseCents6 },
                { group: premiumGroup, rateCents6: rateCents6 - baseCents6 },
              ],
      };
    };
    return buildNativeEvaluationTrace({
      input,
      breakdown,
      segments: classified.segments,
      overtimeMilliseconds: 0,
      price,
    });
  }

  calculate(input: ShiftPayInput, ctx?: RateContext): ShiftPayBreakdown {
    if (!ctx) {
      return applyDstStartOrdinaryTopUp(legacyCalculateShiftPay(input), input, this.awardCode);
    }
    const override = buildOverrideFromContext(ctx);
    const employment = input.employmentType === 'casual' ? 'CASUAL' : 'PERMANENT';
    const coverageGaps = collectSchadsEbaCoverageGaps(ctx, input, employment);
    const breakdown = removeMissingRequiredAllowanceFallbackLines(
      legacyCalculateShiftPay(input, override),
      coverageGaps,
    );
    return applyDstStartOrdinaryTopUp(
      { ...breakdown, ...coverageGapsField(coverageGaps) },
      input,
      this.awardCode,
    );
  }

  calculateWithOvertime(input: ShiftPayWithOvertimeInput, ctx?: RateContext): ShiftPayBreakdown {
    if (!ctx) {
      return applyDstStartOrdinaryTopUp(
        legacyCalculateShiftPayWithOvertime(input),
        input,
        this.awardCode,
      );
    }
    const override = buildOverrideFromContext(ctx);
    const employment = input.employmentType === 'casual' ? 'CASUAL' : 'PERMANENT';
    const coverageGaps = collectSchadsEbaCoverageGaps(ctx, input, employment);
    const breakdown = removeMissingRequiredAllowanceFallbackLines(
      legacyCalculateShiftPayWithOvertime(input, override),
      coverageGaps,
    );
    return applyDstStartOrdinaryTopUp(
      { ...breakdown, ...coverageGapsField(coverageGaps) },
      input,
      this.awardCode,
    );
  }

  calculateRecall(
    input: RecallPayInput,
    ctx: RateContext,
    baseHourlyRate: number,
    employmentType: 'PERMANENT' | 'CASUAL',
  ): RecallPayBreakdown {
    return calculateRecallPay(input, SCHADS_RECALL_RULES, ctx, baseHourlyRate, employmentType);
  }
}

export const schadsEngine = new SchadsEngine();
